import React, { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import PrintIcon from '@mui/icons-material/Print'
import { supabase } from '../../supabaseClient'
import {
  J, ReflectionBlock, ViewToggle, AiEngagement, FeedbackRounds, handlePrint, fmtDate, fmtDateTime,
  StepReflection, JournalNote, UsageRow, FeedbackRow,
} from '../../components/journal/paper'
import StepReflectionCard from '../../components/journal/StepReflectionCard'

// Step 2 research journal — curated Journal view (activities + outputs) and a
// Documentation (audit) view (mentor review record, AI engagement + ISEF
// disclosure). Built on the shared journal paper kit.

type Track = 'scientific_method' | 'engineering_design'

interface DraftPayload {
  iv?: string; dv?: string; cv?: string[]; question?: string
  user_context?: string; need?: string; constraints?: string[]; success_criteria?: string[]; problem_statement?: string
  method_categories?: string[]; method?: string
}
interface ReviewEntry { action: string; note: string | null; acted_at: string }

const TRACK_LABEL: Record<Track, string> = {
  scientific_method: 'Scientific Method',
  engineering_design: 'Engineering Design',
}
const AI_LABELS: Record<string, string> = {
  'step2-evaluate-track-commitment': 'Research approach alignment check',
  'step2-research-type-examples':    'Research approach examples',
  'step2-evaluate-draft':            'Draft feedback',
}
const FIELD_LABELS: Record<string, string> = {
  iv: 'Independent variable', dv: 'Dependent variable', cv: 'Controlled variables',
  question: 'Research question', method: 'Method / approach',
  user_context: 'User & context', need: 'Need', constraints: 'Constraints',
  success_criteria: 'Success criteria', problem_statement: 'Problem statement',
}

export default function JournalTab({ projectId, studentId }: { projectId: string; studentId: string }) {
  const [track,      setTrack]      = useState<Track>('scientific_method')
  const [rationale,  setRationale]  = useState<string | null>(null)
  const [payload,    setPayload]    = useState<DraftPayload | null>(null)
  const [status,     setStatus]     = useState<string>('draft')
  const [mentorNote, setMentorNote] = useState<string | null>(null)
  const [topic,      setTopic]      = useState<string | null>(null)
  const [notes,      setNotes]      = useState<JournalNote[]>([])
  const [reflection, setReflection] = useState<StepReflection | null>(null)
  const [reviewLog,  setReviewLog]  = useState<ReviewEntry[]>([])
  const [usage,      setUsage]      = useState<UsageRow[]>([])
  const [feedback,   setFeedback]   = useState<FeedbackRow[]>([])
  const [updatedAt,  setUpdatedAt]  = useState<string | null>(null)
  const [loading,    setLoading]    = useState(true)
  const [view,       setView]       = useState<'journal' | 'documentation'>('journal')

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ data: rq }, { data: summary }, { data: noteRows }, { data: refl }, { data: log }, { data: usageRows }, { data: fbRows }] = await Promise.all([
        supabase.from('research_question').select('track_committed, track_rationale, draft_payload, status, mentor_note, updated_at').eq('project_id', projectId).maybeSingle(),
        supabase.from('step1_summary').select('confirmed_topic').eq('project_id', projectId).maybeSingle(),
        supabase.from('journal_note').select('note_id, step_number, source_label, content, saved_at')
          .eq('project_id', projectId).eq('step_number', 2).order('saved_at'),
        supabase.from('step_reflection').select('*').eq('project_id', projectId).eq('step_number', 2).maybeSingle(),
        supabase.from('mentor_review_log').select('action, note, acted_at').eq('project_id', projectId).in('action', ['comment', 'approve']).order('acted_at'),
        supabase.from('ai_usage_log').select('function_name, model, input_tokens, output_tokens').eq('project_id', projectId).like('function_name', 'step2%'),
        supabase.from('ai_feedback_log').select('field, evaluation, created_at').eq('project_id', projectId).eq('step_number', 2).order('created_at'),
      ])
      if (cancelled) return
      setTrack(((rq as any)?.track_committed as Track) ?? 'scientific_method')
      setRationale(((rq as any)?.track_rationale ?? null))
      setPayload(((rq as any)?.draft_payload ?? {}) as DraftPayload)
      setStatus(((rq as any)?.status ?? 'draft'))
      setMentorNote(((rq as any)?.mentor_note ?? null))
      setUpdatedAt((rq as any)?.updated_at ?? null)
      setTopic((summary as any)?.confirmed_topic ?? null)
      setNotes((noteRows as JournalNote[] | null) ?? [])
      setReflection((refl as StepReflection | null) ?? null)
      setReviewLog((log as ReviewEntry[] | null) ?? [])
      setUsage((usageRows as UsageRow[] | null) ?? [])
      setFeedback((fbRows as FeedbackRow[] | null) ?? [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [projectId, studentId])

  async function reloadReflection() {
    const { data: refl } = await supabase.from('step_reflection').select('*')
      .eq('project_id', projectId).eq('step_number', 2).maybeSingle()
    setReflection((refl as StepReflection | null) ?? null)
  }

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>

  const p        = payload ?? {}
  const isEng    = track === 'engineering_design'
  const qLabel   = isEng ? 'Problem statement' : 'Research question'
  const headline = (isEng ? p.problem_statement : p.question)?.trim()
  const method   = [(p.method_categories ?? []).join(', '), (p.method ?? '').trim()].filter(Boolean).join(' — ')
  const dateStr  = updatedAt ? fmtDate(updatedAt) : fmtDate(new Date().toISOString())
  const complete = status === 'approved_draft' || status === 'completed'
  const isDoc    = view === 'documentation'

  const defineFields: Array<{ h: string; body?: string }> = isEng
    ? [
        { h: 'The user and context', body: p.user_context },
        { h: 'The need', body: p.need },
        { h: 'Constraints', body: (p.constraints ?? []).filter(Boolean).join('\n') },
        { h: 'Success criteria', body: (p.success_criteria ?? []).filter(Boolean).join('\n') },
      ]
    : [
        { h: 'Independent variable', body: p.iv },
        { h: 'Dependent variable', body: p.dv },
        { h: 'Controlled variables', body: (p.cv ?? []).filter(Boolean).join('\n') },
      ]

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {isDoc ? 'Research Documentation' : 'Your Research Journal'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {isDoc
              ? 'Complete system record for this step — mentor review, AI engagement, and ISEF Rule 4.2 disclosure. Use this for instructor audit or to document your AI use.'
              : `A curated record of how you defined your ${isEng ? 'problem' : 'research question'} — the approach you chose, the decisions you made, and what you learned. Print it for your notebook, or copy what's meaningful.`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <ViewToggle value={view} onChange={setView} />
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => handlePrint('journal-step2-print')}>Print</Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <StepReflectionCard
          projectId={projectId}
          stepNumber={2}
          stepLabel={`Step 2: ${isEng ? 'Problem Statement' : 'Research Question'}`}
          onSavedChange={() => { reloadReflection() }}
        />
      </Box>

      <J.Page id="journal-step2-print">
        <J.Header
          title={isDoc ? 'STEP 2 DOCUMENTATION' : 'SCIENCE RESEARCH JOURNAL'}
          subtitle={isDoc ? `Complete record — ${qLabel}` : `Step 2 — ${qLabel}`}
          date={dateStr}
        />

        {topic && (
          <>
            <J.Section>STARTING FROM MY TOPIC</J.Section>
            <J.Body>Building on the topic my mentor and I settled on in Step 1:</J.Body>
            <J.Quote>{topic}</J.Quote>
          </>
        )}

        <J.Section>CHOOSING MY RESEARCH APPROACH</J.Section>
        <J.Body>
          I committed to the <strong>{TRACK_LABEL[track]}</strong> approach for this project
          {isEng
            ? ' — I am designing and building a solution to a real problem.'
            : ' — I am investigating a testable relationship through controlled experimentation.'}
        </J.Body>
        {rationale?.trim() && (
          <>
            <J.Label>Why this approach fits my project</J.Label>
            <J.Quote>{rationale}</J.Quote>
          </>
        )}

        <J.Section>{isEng ? 'DEFINING MY PROBLEM' : 'DEFINING MY RESEARCH QUESTION'}</J.Section>
        <J.Body>
          {isEng
            ? 'I worked through who the solution is for, what they need, and the constraints and success criteria it must meet:'
            : 'I identified the variables in my investigation and framed them into a specific, testable question:'}
        </J.Body>
        {defineFields.map(f => f.body?.trim() && (
          <Box key={f.h} className="journal-no-break" sx={{ mb: 1 }}>
            <J.Label>{f.h}</J.Label>
            <J.Body sx={{ whiteSpace: 'pre-wrap', mb: 0 }}>{f.body}</J.Body>
          </Box>
        ))}
        <Box sx={{ mt: 1.5 }}>
          <J.Label>My {qLabel.toLowerCase()}</J.Label>
          <J.Body sx={{ fontWeight: 'bold', fontSize: '12pt' }}>{headline || '—'}</J.Body>
        </Box>

        {method.trim() && (
          <>
            <J.Section>HOW I PLAN TO INVESTIGATE IT</J.Section>
            <J.Body sx={{ whiteSpace: 'pre-wrap' }}>{method}</J.Body>
          </>
        )}

        <ReflectionBlock refl={reflection} notes={notes} />

        {isDoc && (mentorNote || reviewLog.length > 0) && (
          <>
            <J.Section>MENTOR REVIEW RECORD</J.Section>
            {reviewLog.map((r, i) => (
              <Box key={i} className="journal-no-break" sx={{ mb: 1 }}>
                <J.Label>{r.action === 'approve' ? 'Approved' : 'Sent back with comments'} · {fmtDateTime(r.acted_at)}</J.Label>
                {r.note && <J.Quote>{r.note}</J.Quote>}
              </Box>
            ))}
            {mentorNote && reviewLog.length === 0 && <J.Quote>{mentorNote}</J.Quote>}
          </>
        )}

        {isDoc && <FeedbackRounds rows={feedback} fieldLabels={FIELD_LABELS} />}

        {isDoc && <AiEngagement usage={usage} labels={AI_LABELS} date={dateStr} />}

        {complete && <J.Signatures />}

        {!complete && (
          <J.Body sx={{ mt: 2, fontSize: '10pt', color: '#555', fontStyle: 'italic' }}>
            This entry updates as you refine your work. The final version — with signatures — is ready once your
            mentor approves this step.
          </J.Body>
        )}
      </J.Page>

      {!topic && !headline && (
        <Alert severity="info" sx={{ mt: 2 }}>Your Step 2 journal fills in as you complete your work on this step.</Alert>
      )}
    </Box>
  )
}
